import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    addDoc,
    collection,
    doc,
    onSnapshot,
    query,
    serverTimestamp,
    updateDoc,
    where
} from "firebase/firestore";
import { db } from "../firebase";

function ReservationClient() {
    const [rooms, setRooms] = useState([]);
    const [reservations, setReservations] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [reservationItems, setReservationItems] = useState([]);
    const [formValues, setFormValues] = useState({
        guestName: "",
        guestPhone: "",
        guestEmail: "",
        roomGroupName: "",
        checkIn: "",
        checkOut: "",
        guestsCount: "",
        paymentMethodId: "",
        notes: "",
        reservationItemsQty: {} // { [itemId]: quantity }
    });
    const [status, setStatus] = useState({ type: "", message: "" });
    const [saving, setSaving] = useState(false);
    const [step, setStep] = useState("form");

    useEffect(() => {
        const unsubscribe = onSnapshot(collection(db, "priceList"), (snapshot) => {
            const nextRooms = snapshot.docs
                .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
                .filter((item) => (item.type || "room") === "room");
            const nextPayments = snapshot.docs
                .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
                .filter((item) => item.type === "payment");
            const nextReservationItems = snapshot.docs
                .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
                .filter((item) => item.type === "reservation");
            setRooms(nextRooms);
            setPaymentMethods(nextPayments);
            setReservationItems(nextReservationItems);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (formValues.paymentMethodId) {
            return;
        }
        const defaultMethod = paymentMethods.find((method) => method.isDefault);
        if (!defaultMethod?.id) {
            return;
        }
        setFormValues((prev) => ({
            ...prev,
            paymentMethodId: defaultMethod.id
        }));
    }, [paymentMethods, formValues.paymentMethodId]);

    useEffect(() => {
        const reservationsQuery = query(
            collection(db, "reservations"),
            where("stayType", "==", "room")
        );
        const unsubscribe = onSnapshot(reservationsQuery, (snapshot) => {
            const items = snapshot.docs.map((docSnap) => ({
                id: docSnap.id,
                ...docSnap.data()
            }));
            setReservations(items);
        });

        return () => unsubscribe();
    }, []);

    const roomOptions = useMemo(() => {
        const byGroupName = new Map();
        rooms.forEach((room) => {
            const groupName = String(room.groupName || room.clientName || "").trim();
            if (!groupName) {
                return;
            }
            if (!byGroupName.has(groupName)) {
                byGroupName.set(groupName, []);
            }
            byGroupName.get(groupName).push(room);
        });
        return Array.from(byGroupName.entries()).map(([groupName, items]) => ({
            groupName,
            rooms: items
        }));
    }, [rooms]);

    // Helper: check if two date ranges overlap
    const isOverlap = (startA, endA, startB, endB) => {
        if (!startA || !endA || !startB || !endB) return false;
        const aStart = new Date(startA).getTime();
        const aEnd = new Date(endA).getTime();
        const bStart = new Date(startB).getTime();
        const bEnd = new Date(endB).getTime();
        return aStart < bEnd && bStart < aEnd;
    };

    // Only count occupancy for reservations that overlap with the selected date range
    const occupancyByRoomId = useMemo(() => {
        const counts = new Map();
        rooms.forEach((room) => {
            counts.set(room.id, 0);
        });

        const getReservationGuests = (reservation) => {
            if (Array.isArray(reservation.roomAssignments) && reservation.roomAssignments.length) {
                return reservation.roomAssignments.reduce((total, item) => {
                    const guests = Number(item?.guestsCount) || 0;
                    return total + guests;
                }, 0);
            }
            const guests = Number(reservation.guestsCount);
            return Number.isFinite(guests) ? guests : 1;
        };

        reservations.forEach((reservation) => {
            // Only consider reservations that overlap with the selected date range
            if (!isOverlap(
                formValues.checkIn,
                formValues.checkOut,
                reservation.checkIn,
                reservation.checkOut
            )) {
                return;
            }
            const guestCount = getReservationGuests(reservation);
            if (reservation.roomId && counts.has(reservation.roomId)) {
                counts.set(reservation.roomId, (counts.get(reservation.roomId) || 0) + guestCount);
                return;
            }
            if (reservation.roomNumber) {
                const match = rooms.find(
                    (room) => String(room.name || "").trim() === String(reservation.roomNumber).trim()
                );
                if (match?.id && counts.has(match.id)) {
                    counts.set(match.id, (counts.get(match.id) || 0) + guestCount);
                }
            }
        });

        return counts;
    }, [reservations, rooms, formValues.checkIn, formValues.checkOut]);

    // Calculate group availability considering pay per person/room and only free rooms for pay per room
    const groupAvailabilityByName = useMemo(() => {
        const availability = new Map();
        rooms.forEach((room) => {
            const groupName = String(room.groupName || room.clientName || "").trim();
            if (!groupName) {
                return;
            }
            const limit = Number(room.personLimit) || 0;
            const occupiedCount = occupancyByRoomId.get(room.id) || 0;
            let availableInRoom = 0;
            if (room.applyPerPerson) {
                // Pay per person: free beds in this room
                availableInRoom = Math.max(0, limit - occupiedCount);
            } else {
                // Pay per room: only count if room is completely free
                availableInRoom = occupiedCount === 0 ? limit : 0;
            }
            const current = availability.get(groupName) || { capacity: 0, occupied: 0, available: 0 };
            availability.set(groupName, {
                capacity: current.capacity + limit,
                occupied: current.occupied + occupiedCount,
                available: current.available + availableInRoom
            });
        });
        // available is already calculated per group
        return availability;
    }, [rooms, occupancyByRoomId]);


    const getNights = (start, end) => {
        if (!start || !end) {
            return 0;
        }
        const startDate = new Date(start);
        const endDate = new Date(end);
        const diffMs = endDate.getTime() - startDate.getTime();
        if (Number.isNaN(diffMs) || diffMs <= 0) {
            return 0;
        }
        return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    };

    const getRoomCost = (room, guests, nights) => {
        if (!room || !nights || !guests) {
            return 0;
        }
        const price = Number(room.price) || 0;
        if (room.applyPerPerson) {
            return price * guests * nights;
        }
        return price * nights;
    };

    const addWorkingDays = (value, daysToAdd) => {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return null;
        }
        let added = 0;
        const next = new Date(date);
        while (added < daysToAdd) {
            next.setDate(next.getDate() + 1);
            const day = next.getDay();
            if (day !== 0 && day !== 6) {
                added += 1;
            }
        }
        return next;
    };

    const formatDate = (value) => {
        if (!value) {
            return "-";
        }
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return "-";
        }
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const selectedRoom = useMemo(() => {
        const roomGroupName = formValues.roomGroupName.trim();
        if (!roomGroupName) {
            return null;
        }
        const option = roomOptions.find((item) => item.groupName === roomGroupName);
        if (!option || option.rooms.length === 0) {
            return null;
        }
        const sortedRooms = [...option.rooms].sort((a, b) => {
            const aCount = occupancyByRoomId.get(a.id) || 0;
            const bCount = occupancyByRoomId.get(b.id) || 0;
            return aCount - bCount;
        });
        return sortedRooms[0];
    }, [formValues.roomGroupName, occupancyByRoomId, roomOptions]);

    // Helper: get reservation item rules (copied from admin logic)
    const getReservationItemRules = (item) => {
        const price = Number(item?.price);
        const fallbackPerDay = Number(item?.pricePerReservation);
        const fallbackPerPerson = Number(item?.pricePerPerson);
        const applyPerDay = typeof item?.applyPerDay === "boolean"
            ? item.applyPerDay
            : Number.isFinite(fallbackPerDay) && fallbackPerDay > 0;
        const applyPerPerson = typeof item?.applyPerPerson === "boolean"
            ? item.applyPerPerson
            : Number.isFinite(fallbackPerPerson) && fallbackPerPerson > 0;
        const resolvedPrice = Number.isFinite(price)
            ? price
            : Number.isFinite(fallbackPerDay)
                ? fallbackPerDay
                : Number.isFinite(fallbackPerPerson)
                    ? fallbackPerPerson
                    : 0;
        return {
            price: resolvedPrice,
            applyPerDay,
            applyPerPerson
        };
    };

    // Helper: get reservation items cost for the group
    const getReservationItemsCost = (guests, nights) => {
        if (!formValues.roomGroupName) return 0;
        return reservationItems.reduce((sum, item) => {
            const qty = Number(formValues.reservationItemsQty[item.id]) || 0;
            if (qty < 1) return sum;
            const rules = getReservationItemRules(item);
            let cost = 0;
            if (rules.applyPerPerson) {
                cost += rules.price * guests * nights * qty;
            } else if (rules.applyPerDay) {
                cost += rules.price * nights * qty;
            } else {
                cost += rules.price * qty;
            }
            return sum + cost;
        }, 0);
    };

    const getSelectedReservationItemsSnapshot = () =>
        Object.entries(formValues.reservationItemsQty || {})
            .filter(([, qty]) => Number(qty) > 0)
            .map(([itemId, qty]) => {
                const item = reservationItems.find((i) => i.id === itemId);
                if (!item) {
                    return null;
                }
                const rules = getReservationItemRules(item);
                return {
                    id: item.id,
                    name: item.name || "",
                    quantity: Number(qty),
                    price: rules.price,
                    applyPerDay: rules.applyPerDay,
                    applyPerPerson: rules.applyPerPerson
                };
            })
            .filter(Boolean);

    const totalPricePln = useMemo(() => {
        const guests = Number(formValues.guestsCount) || 0;
        const nights = getNights(formValues.checkIn, formValues.checkOut);
        const roomCost = getRoomCost(selectedRoom, guests, nights);
        const reservationItemsCost = getReservationItemsCost(guests, nights);
        return Math.round((roomCost + reservationItemsCost) * 100) / 100;
    }, [formValues.checkIn, formValues.checkOut, formValues.guestsCount, selectedRoom, reservationItems, formValues.roomGroupName, formValues.reservationItemsQty]);

    const paymentMethod = useMemo(
        () => paymentMethods.find((item) => item.id === formValues.paymentMethodId) || null,
        [paymentMethods, formValues.paymentMethodId]
    );

    const depositAmount = useMemo(() => {
        const percent = Number(paymentMethod?.depositPercent) || 0;
        return Math.round(totalPricePln * (percent / 100) * 100) / 100;
    }, [paymentMethod, totalPricePln]);

    const paymentDueDate = useMemo(() => {
        const dueDate = addWorkingDays(new Date(), 3);
        return dueDate ? formatDate(dueDate) : "-";
    }, []);

    const handleChange = (event) => {
        const { name, value, dataset } = event.target;
        // Formating the phone number input to have spaces after every 3 digits
        if (name === "guestPhone") {
            // keep only digits
            // const digits = value.replace(/\D/g, "");
            const digits = value.replace(/(?!^\+)\D/g, "");

            // format: ddd ddd ddd
            const formatted =
                digits.length <= 3
                    ? digits
                    : digits.length <= 6
                        ? `${digits.slice(0, 3)} ${digits.slice(3)}`
                        : `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9, 12)}`;

            setFormValues((prev) => ({
                ...prev,
                guestPhone: formatted
            }));
            return;
        }
        // End of Formating the phone number input to have spaces after every 3 digits

        // For reservation item quantity fields
        if (dataset && dataset.resitemid) {
            setFormValues((prev) => {
                // Create a new object to ensure useMemo dependencies update
                const newReservationItemsQty = {
                    ...prev.reservationItemsQty,
                    [dataset.resitemid]: value
                };
                return {
                    ...prev,
                    reservationItemsQty: newReservationItemsQty
                };
            });
            return;
        }
        setFormValues((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setStatus({ type: "", message: "" });

        const guestName = formValues.guestName.trim();
        const guestPhone = formValues.guestPhone.trim();
        const guestEmail = formValues.guestEmail.trim();
        const roomGroupName = formValues.roomGroupName.trim();
        const checkIn = formValues.checkIn;
        const checkOut = formValues.checkOut;
        const guestsCount = Number(formValues.guestsCount);
        const notes = formValues.notes.trim();
        const nights = getNights(checkIn, checkOut);
        const paymentMethodId = formValues.paymentMethodId;

        if (!guestName || !guestPhone || !guestEmail) {
            setStatus({ type: "error", message: "Uzupełnij wszystkie dane." });
            return;
        }

        if (!checkIn || !checkOut) {
            setStatus({ type: "error", message: "Wybierz daty pobytu." });
            return;
        }

        // Zameldowanie cannot be before today
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const checkInDate = new Date(checkIn);
        if (checkInDate < today) {
            setStatus({ type: "error", message: "Zameldowanie nie może być wcześniej niż dzisiaj." });
            return;
        }

        if (checkInDate >= new Date(checkOut)) {
            setStatus({
                type: "error",
                message: "Wymeldowanie musi być później niż zameldowanie."
            });
            return;
        }

        if (!Number.isFinite(guestsCount) || guestsCount <= 0) {
            setStatus({ type: "error", message: "Podaj liczbę osób." });
            return;
        }

        if (!roomGroupName) {
            setStatus({ type: "error", message: "Wybierz kategorię." });
            return;
        }

        if (!paymentMethodId) {
            setStatus({ type: "error", message: "Wybierz metodę płatności." });
            return;
        }

        const option = roomOptions.find(
            (item) => item.groupName === roomGroupName
        );
        if (!option || option.rooms.length === 0) {
            setStatus({ type: "error", message: "Nie znaleziono pokoju." });
            return;
        }

        const availability = groupAvailabilityByName.get(roomGroupName);
        const availableCount = Number(availability?.available) || 0;
        if (availableCount <= 0) {
            setStatus({ type: "error", message: "Brak wolnych miejsc w tej kategorii." });
            return;
        }
        if (guestsCount > availableCount) {
            setStatus({
                type: "error",
                message: `Dostępne miejsca w kategorii: ${availableCount}.`
            });
            return;
        }

        const sortedRooms = [...option.rooms].sort((a, b) => {
            const aCount = occupancyByRoomId.get(a.id) || 0;
            const bCount = occupancyByRoomId.get(b.id) || 0;
            return aCount - bCount;
        });
        const chosenRoom = sortedRooms[0];
        const totalPrice = Math.round(getRoomCost(chosenRoom, guestsCount, nights) * 100) / 100;

        if (step === "form") {
            setStep("confirm");
            return;
        }

        setSaving(true);
        try {
            const reservationItemsSnapshot = getSelectedReservationItemsSnapshot();
            const reservationRef = await addDoc(collection(db, "reservations"), {
                stayType: "room",
                guestName,
                guestPhone,
                guestEmail,
                checkIn,
                checkOut,
                roomId: chosenRoom.id,
                roomNumber: chosenRoom.name || "",
                roomGroupName,
                guestsCount,
                paymentMethodId,
                depositAmount,
                depositDueDate: paymentDueDate,
                totalPricePln: totalPrice,
                reservationItems: reservationItemsSnapshot,
                notes,
                note: notes,
                confirmationToken: "",
                confirmationLink: "",
                confirmationConfirmed: false,
                confirmationConfirmedAt: "",
                roomAssignments: [
                    { roomNumber: chosenRoom.name || "", guestsCount }
                ],
                createdAt: serverTimestamp()
            });
            await updateDoc(doc(db, "reservations", reservationRef.id), {
                reservationId: reservationRef.id
            });

            // Send webhook to n8n
            try {
                // test version n8n
                // await fetch("https://jquery.7776.link/webhook-test/d325ae95-ef69-49d7-8499-fc0e7271f268", {            
                // await fetch("https://jquery.7776.link/webhook/confirmation-email", {
                   await fetch("https://jquery.7776.link/webhook/confirmation-email", {
                    
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({
                        reservationId: reservationRef.id,
                        documentId: reservationRef.id,
                        guestName,
                        guestPhone,
                        guestEmail,
                        recipientEmail: guestEmail,
                        checkIn,
                        checkOut,
                        roomGroupName,
                        roomNumber: chosenRoom.name || "",
                        roomAssignments: [
                            {
                                roomNumber: chosenRoom.name || "",
                                guestsCount
                            }
                        ],
                        guestsCount,
                        paymentMethodId,
                        paymentMethodName: paymentMethod?.name || "",
                        reservationItems: Object.entries(formValues.reservationItemsQty || {})
                            .filter(([, qty]) => Number(qty) > 0)
                            .map(([itemId, qty]) => {
                                const item = reservationItems.find((i) => i.id === itemId);
                                return item ? { name: item.name, quantity: Number(qty) } : null;
                            })
                            .filter(Boolean),
                        notes,
                        depositAmount,
                        depositDueDate: paymentDueDate,
                        totalPricePln: totalPrice
                    })
                });
            } catch (webhookError) {
                // Optionally handle webhook error (e.g., log or show warning)
            }

            setFormValues({
                guestName: "",
                guestPhone: "",
                guestEmail: "",
                roomGroupName: "",
                checkIn: "",
                checkOut: "",
                guestsCount: "",
                paymentMethodId: "",
                notes: "",
                reservationItemsQty: {}
            });
            setStep("done");
            setStatus({ type: "", message: "" });
        } catch (error) {
            setStatus({
                type: "error",
                message: error.message || "Nie udało się wysłać rezerwacji."
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="ClientLoginPage">
            <div className="Card ClientLoginCard ClientReservationCard">
                {/* <p className="Subtitle">Uzupelnij dane, aby wyslac rezerwacje.</p> */}

                {step === "done" ? (
                    <>
                        <h1 className="Title">Rezerwacja pokoju zostala wyslana</h1>
                        <p className="Success">Dziekujemy za zlozone zamowienie</p>
                        <Link to="/" className="Button" style={{ marginTop: 24, display: "inline-block", textAlign: "center" }}>
                            Strona główna
                        </Link>
                    </>
                ) : (
                    <form className="Form" onSubmit={handleSubmit}>
                        {step === "form" ? (
                            <>
                                <div className="FormGrid">
                                    <label className="Field">
                                        <span className="Label">Imie i nazwisko</span>
                                        <input
                                            className="Input"
                                            name="guestName"
                                            value={formValues.guestName}
                                            onChange={handleChange}
                                            required
                                        />
                                    </label>

                                    <label className="Field">
                                        <span className="Label">Telefon</span>
                                        <input
                                            className="Input"
                                            name="guestPhone"
                                            value={formValues.guestPhone}
                                            onChange={handleChange}
                                            placeholder="123 456 789"
                                            required
                                        />
                                    </label>
                                    <label className="Field">
                                        <span className="Label">Email</span>
                                        <input
                                            className="Input"
                                            type="email"
                                            name="guestEmail"
                                            value={formValues.guestEmail}
                                            onChange={handleChange}
                                            required
                                        />
                                    </label>
                                </div>

                                <div className="FormSectionSpacer" aria-hidden="true" />

                                <div className="FormGrid">
                                    <label className="Field CompactField">
                                        <span className="Label">Zameldowanie</span>
                                        <input
                                            className="Input"
                                            type="date"
                                            name="checkIn"
                                            value={formValues.checkIn}
                                            onChange={handleChange}
                                            required
                                        />
                                    </label>
                                    <label className="Field CompactField">
                                        <span className="Label">Wymeldowanie</span>
                                        <input
                                            className="Input"
                                            type="date"
                                            name="checkOut"
                                            value={formValues.checkOut}
                                            onChange={handleChange}
                                            required
                                        />
                                    </label>
                                    <label className="Field CompactField">
                                        <span className="Label">Kategoria</span>
                                        <select
                                            className="Input"
                                            name="roomGroupName"
                                            value={formValues.roomGroupName}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Wybierz kategorię</option>
                                            {roomOptions.map((option) => (
                                                <option key={option.groupName} value={option.groupName}>
                                                    {option.groupName}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="Field CompactField">
                                        <span className="Label">Liczba osob</span>
                                        <input
                                            className="Input SmallNumberInput"
                                            type="number"
                                            name="guestsCount"
                                            min="1"
                                            step="1"
                                            value={formValues.guestsCount}
                                            onChange={handleChange}
                                            required
                                        />
                                    </label>
                                    {reservationItems.length > 0 && (
                                        <div style={{ width: "100%" }}>
                                            <span className="Label">Pozycje do rezerwacji (ilość):</span>
                                            {reservationItems.map(item => {
                                                const guests = Number(formValues.guestsCount) || 0;
                                                return (
                                                    <label key={item.id} className="Field CompactField" style={{ display: "block" }}>
                                                        <span>{item.name} ({item.price}zł) </span>
                                                        <input
                                                            className="Input SmallNumberInput"
                                                            type="number"
                                                            min="0"
                                                            max={guests}
                                                            step="1"
                                                            value={formValues.reservationItemsQty[item.id] || ""}
                                                            data-resitemid={item.id}
                                                            onChange={handleChange}
                                                            placeholder="0"
                                                        />
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}
                                    <label className="Field CompactField">
                                        <span className="Label">Uwagi do rezerwacji</span>
                                        <textarea
                                            className="Input"
                                            name="notes"
                                            value={formValues.notes}
                                            onChange={handleChange}
                                            placeholder="Uwagi do rezerwacji (opcjonalne)"
                                            rows={4}
                                        />
                                    </label>
                                </div>

                                <div className="FormSectionSpacer" aria-hidden="true" />

                                <div className="FormGrid">
                                    <label className="Field CompactField">
                                        <span className="Label">Metoda platnosci</span>
                                        <select
                                            className="Input"
                                            name="paymentMethodId"
                                            value={formValues.paymentMethodId}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Wybierz metode</option>
                                            {paymentMethods.map((method) => (
                                                <option key={method.id} value={method.id}>
                                                    {method.name}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                </div>

                                <div className="SummaryRow SummaryRowCompact">
                                    <div className="SummaryItem">
                                        <span className="SummaryLabel">Cena</span>
                                        <span>{totalPricePln > 0 ? `${totalPricePln} PLN` : "-"}</span>
                                    </div>
                                    <div className="SummaryItem">
                                        <span className="SummaryLabel">Zadatek</span>
                                        <span>{depositAmount > 0 ? `${depositAmount} PLN` : "-"}</span>
                                    </div>
                                    <div className="SummaryItem">
                                        <span className="SummaryLabel">Termin platnosci</span>
                                        <span>{paymentDueDate}</span>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="SummaryRow" style={{ borderBottom: '1px solid #ccc' }}>
                                    <span>Imie i nazwisko</span>
                                    <span>{formValues.guestName || "-"}</span>
                                </div>
                                <div className="SummaryRow" 
                                    style={{ borderBottom: '1px solid #ccc' }}
                                >
                                    <span>Telefon</span>
                                    <span>{formValues.guestPhone || "-"}</span>
                                </div>
                                <div className="SummaryRow" style={{ borderBottom: '1px solid #ccc' }}>
                                    <span>Email</span>
                                    <span>{formValues.guestEmail || "-"}</span>
                                </div>
                                <div className="SummaryRow" style={{ borderBottom: '1px solid #ccc' }}>
                                    <span>Zameldowanie</span>
                                    <span>{formatDate(formValues.checkIn)}</span>
                                </div>
                                <div className="SummaryRow" style={{ borderBottom: '1px solid #ccc' }}>
                                    <span>Wymeldowanie</span>
                                    <span>{formatDate(formValues.checkOut)}</span>
                                </div>
                                <div className="SummaryRow" style={{ borderBottom: '1px solid #ccc' }}>
                                    <span>Liczba osob</span>
                                    <span>{formValues.guestsCount || "-"}</span>
                                </div>
                                
                                {reservationItems.length > 0 && (
                                    <div className="SummaryRow" style={{ borderBottom: '1px solid #ccc' }}  >
                                        <span>Pozycje</span>
                                        <span>
                                            {Object.entries(formValues.reservationItemsQty || {})
                                                .filter(([itemId, qty]) => Number(qty) > 0)
                                                .map(([itemId, qty]) => {
                                                    const item = reservationItems.find(i => i.id === itemId);
                                                    return item ? `${item.name} (${qty})` : null;
                                                })
                                                .filter(Boolean)
                                                .join(', ') || "-"}
                                        </span>
                                    </div>
                                )}
                                <div className="SummaryRow" style={{ borderBottom: '1px solid #ccc' }}>
                                    <span>Uwagi do rezerwacji</span>
                                    <span>{formValues.notes || "-"}</span>
                                </div>
                                <div className="SummaryRow" style={{ borderBottom: '1px solid #ccc' }}  >
                                    <span>Kategoria</span>
                                    <span>{formValues.roomGroupName || "-"}</span>
                                </div>
                                <div className="SummaryRow" style={{ borderBottom: '1px solid #ccc' }}>
                                    <span>Metoda platnosci</span>
                                    <span>{paymentMethod?.name || "-"}</span>
                                </div>
                                <div className="SummaryRow SummaryRowCompact">
                                    <div className="SummaryItem">
                                        <span className="SummaryLabel">Cena</span>
                                        <span>{totalPricePln > 0 ? `${totalPricePln} PLN` : "-"}</span>
                                    </div>
                                    <div className="SummaryItem">
                                        <span className="SummaryLabel">Zadatek</span>
                                        <span>{depositAmount > 0 ? `${depositAmount} PLN` : "-"}</span>
                                    </div>
                                
                                    <div className="SummaryItem">
                                        <span className="SummaryLabel">Termin platnosci</span>
                                        <span>{paymentDueDate}</span>
                                    </div>
                                </div>
                            </>
                        )}

                        {status.message ? (
                            <p className={status.type === "error" ? "Error" : "Success"}>
                                {status.message}
                            </p>
                        ) : null}

                        {step === "form" ? (
                            <button className="Button" type="submit" disabled={saving}>
                                Przejdz dalej
                            </button>
                        ) : (
                            <div className="TitleRow">
                                <button
                                    className="Button Ghost"
                                    type="button"
                                    onClick={() => setStep("form")}
                                >
                                    Powrot
                                </button>
                                <button className="Button" type="submit" disabled={saving}>
                                    {saving ? "Wysylanie..." : "Potwierdz"}
                                </button>
                            </div>
                        )}
                    </form>
                )}
            </div>
        </div>
    );
}

export default ReservationClient;
